import { ClientsService } from '../../../shared/services/clients.service';
import { Component, OnInit, Input, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';

import { FormEditBaseComponent } from '@components/form-edit-base.component';
import { UiService } from '@services/ui.service';
import { Client } from '@classes/client';
import { DictionaryValuesService } from '@services/dictionaryValues.service';
import { DictionaryValue } from '@classes/dictionary/dictionaryValue';
import { DictionariesService } from '@services/dictionaries.service';
import { Dictionary } from '@classes/dictionary/dictionary';
import { Site } from '@classes/site';
import { SitesService } from '@services/sites.service';

interface CivilityItem { id: number; name: string; }

@Component({
  selector: 'app-dictionary-edit',
  templateUrl: './dictionary-edit.component.html',
  styleUrls: ['./dictionary-edit.component.scss']
})
export class DictionaryEditComponent extends FormEditBaseComponent implements OnInit {
  @Input() dictionaryValue: DictionaryValue;
  @Input() transportTypes: DictionaryValue[];
  @Input() urgencyLevels: DictionaryValue[];
  @Input() lots: DictionaryValue[];
  dictionaries: Dictionary[];
  clients: Client[];
  sites: Site[];
  identifierPattern = "^[0-9]{0,50}$";

  stringMaxLength = 50;

  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private dictionaryValuesSrv: DictionaryValuesService,
    private dictionariesSrv: DictionariesService,
    private clientsSrv: ClientsService,
    private sitesSrv: SitesService,
    private changeDetect: ChangeDetectorRef
  ) {
    super(uiSrv, fb);
  }

  ngOnInit() {
    super.ngOnInit();
    this.initSub();
  }

  initSub(): void {

    this.isLoading = true;

    const dictionariesPromise = new Promise((resolve, reject) => {
      this.dictionariesSrv.getList()
        .subscribe(dictionariesFromSrv => {
          this.dictionaries = dictionariesFromSrv;
          resolve(dictionariesFromSrv);
        });
    });

    const clientsPromise = new Promise((resolve, reject) => {
      this.clientsSrv.getList()
        .subscribe(clientsFromSrv => {
          this.clients = clientsFromSrv;
          resolve(clientsFromSrv);
        });
    });

    Promise.all([dictionariesPromise, clientsPromise])
      .then(() => {
        this.initForm();
        this.isLoading = false;
      });
  }

  onSubmit(): void {
    this.isLoading = true;
    if (this.dictionaryValue) {
      this.update();
    } else {
      this.create();
    }
  }

  onCancel(): void {
    this.editFinished.emit();
  }

  endForm(response: boolean) {
    if (response) {
      this.onSubmit();
    } else {
      this.onCancel();
    }
  }

  private update(): void {
    const dictionaryValueCopy: DictionaryValue = JSON.parse(JSON.stringify(this.dictionaryValue));
    dictionaryValueCopy.name = this.editForm.value.name;
    dictionaryValueCopy.client = this.editForm.value.client;
    dictionaryValueCopy.dictionary = this.editForm.value.dictionary;
    dictionaryValueCopy.sortOrder = this.dictionaryValue.sortOrder;
    if (this.editForm.value.dictionary.code === 'transport-types') {
      this.dictionaryValuesSrv.putTransportType(dictionaryValueCopy).subscribe(() => this.isLoading = false);
    } else if (this.editForm.value.dictionary.code === 'urgency-levels') {
      this.dictionaryValuesSrv.putUrgencyLevel(dictionaryValueCopy).subscribe(() => this.isLoading = false);
    } else if (this.editForm.value.dictionary.code === 'lots') {
      this.dictionaryValuesSrv.putLot(dictionaryValueCopy).subscribe(() => this.isLoading = false);
    }
  }

  private create(): void {
    let sortOrder = 0;

    if (this.editForm.value.dictionary.code === 'transport-types') {
      sortOrder = this.transportTypes.length + 1;
    } else if (this.editForm.value.dictionary.code === 'urgency-levels') {
      sortOrder = this.urgencyLevels.length + 1;
    } else if (this.editForm.value.dictionary.code === 'lots') {
      sortOrder = this.lots.length + 1;
    }

    this.dictionaryValue = new DictionaryValue(
      this.editForm.value.name,
      this.editForm.value.dictionary,
      this.editForm.value.client,
      this.editForm.value.dictionary.code === 'lots' ? this.editForm.value.site : null,
      sortOrder
    );

    if (this.editForm.value.dictionary.code === 'transport-types') {
      this.dictionaryValuesSrv.postTransportType(this.dictionaryValue)
        .subscribe(
          () => this.isLoading = false,
          () => this.dictionaryValue = null
        );
    } else if (this.editForm.value.dictionary.code === 'urgency-levels') {
      this.dictionaryValuesSrv.postUrgencyLevel(this.dictionaryValue)
        .subscribe(
          () => this.isLoading = false,
          () => this.dictionaryValue = null
        );
    } else if (this.editForm.value.dictionary.code === 'lots') {
      this.dictionaryValuesSrv.postLot(this.dictionaryValue)
        .subscribe(
          () => this.isLoading = false,
          () => this.dictionaryValue = null
        );
    }
  }

  private initForm(): void {
    let name = '';
    let client = null;
    let dictionary = null;
    let site = null;

    if (this.dictionaryValue) {
      name = this.dictionaryValue.name;
      dictionary = this.dictionaryValue.dictionary;
      client = this.dictionaryValue.client || null;
      site = this.dictionaryValue.site || null;
    }

    this.editForm = this.fb.group({
      name: [name, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      dictionary: [dictionary, [Validators.required]],
      client: [client, Validators.required],
      site: [site]
    });


    this.addSubscription(
      this.editForm.get('dictionary').valueChanges.subscribe((dictionary) => {
        if (dictionary.code === 'lots') {
          this.editForm.get('site').setValidators(Validators.required)

          this.getSitesByClientId(this.editForm.value?.client?.id);
        } else {
          this.editForm.get('site').clearValidators();
        }
        this.editForm.updateValueAndValidity();
      })
    )

    this.addSubscription(
      this.editForm.get('client').valueChanges.subscribe((client) => {
        this.editForm.get('site').reset();
        this.sites = null;
        if (typeof client !== 'string' && this.editForm.value?.dictionary?.code === "lots") {
          this.getSitesByClientId(client.id);
        }
      })
    )

  }

  getSitesByClientId(clientId: string) {
    if (clientId) {
      this.sitesSrv.getListByClients([clientId]).subscribe((sitesFromSrv) => {
        this.sites = sitesFromSrv;
      })
    }
  }
}
